import type {
  Credit,
  CreditFilters,
  CreditPayment,
  CreditStatusUpdate,
  PaginatedCredits,
  PaginatedResponse,
} from '@/types/credits';

import { getStoredToken } from './apiClient';
import { ApiError, authenticatedRequest } from './base.service';

/**
 * Credits Service
 * Handles all credit/advance-related API endpoints from /api/v1/credits/* and /api/v1/credit-payments/*
 */

export const creditsService = {
  /**
   * Get credits with filters (paginated)
   */
  async getCredits(filters: CreditFilters = {}): Promise<PaginatedResponse<Credit>> {
    const searchParams = new URLSearchParams();

    if (filters.page) searchParams.append('page', filters.page.toString());
    if (filters.limit) searchParams.append('limit', filters.limit.toString());
    if (filters.orderDirection) searchParams.append('orderDirection', filters.orderDirection);
    if (filters.status) searchParams.append('status', filters.status);
    if (filters.type) searchParams.append('type', filters.type);
    if (filters.organization_id) searchParams.append('organization_id', filters.organization_id);
    if (filters.user_id) searchParams.append('user_id', filters.user_id);

    const queryString = searchParams.toString();
    const url = `/api/v1/credits${queryString ? `?${queryString}` : ''}`;

    const token = getStoredToken();
    if (!token) throw new ApiError('No authentication token found', 401);

    return authenticatedRequest<PaginatedResponse<Credit>>(url, {}, token);
  },

  /**
   * Get credits for a specific user (simple paginated format)
   */
  async getCreditsByUser(userId: string, organizationId?: string): Promise<PaginatedCredits> {
    const token = getStoredToken();
    if (!token) throw new ApiError('No authentication token found', 401);

    const orgQuery = organizationId
      ? `&organization_id=${encodeURIComponent(organizationId)}`
      : '';

    return authenticatedRequest<PaginatedCredits>(
      `/api/v1/credits?user_id=${userId}${orgQuery}`,
      {},
      token
    );
  },

  /**
   * Get a single credit by ID
   */
  async getCredit(creditId: string): Promise<Credit> {
    const token = getStoredToken();
    if (!token) throw new ApiError('No authentication token found', 401);

    return authenticatedRequest<Credit>(`/api/v1/credits/${creditId}`, {}, token);
  },

  /**
   * Approve a credit
   */
  async approveCredit(creditId: string): Promise<Credit> {
    const token = getStoredToken();
    if (!token) throw new ApiError('No authentication token found', 401);

    return authenticatedRequest<Credit>(
      `/api/v1/credits/${creditId}/approve`,
      {
        method: 'PATCH',
      },
      token
    );
  },

  /**
   * Update credit status
   */
  async updateCreditStatus(creditId: string, statusUpdate: CreditStatusUpdate): Promise<Credit> {
    const token = getStoredToken();
    if (!token) throw new ApiError('No authentication token found', 401);

    return authenticatedRequest<Credit>(
      `/api/v1/credits/${creditId}/status`,
      {
        method: 'PATCH',
        body: JSON.stringify(statusUpdate),
      },
      token
    );
  },

  /**
   * Get credit payments history for a specific user
   */
  async getCreditPayments(userId: string): Promise<CreditPayment[]> {
    const token = getStoredToken();
    if (!token) throw new ApiError('No authentication token found', 401);

    return authenticatedRequest<CreditPayment[]>(
      `/api/v1/credit-payments?user_id=${userId}`,
      {},
      token
    );
  },
};
